import React, { useMemo, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useNewTranslation } from 'hooks'
import { theme } from 'theme'
import { TeamActions, getTeamsList, TeamState } from 'state/teams'
import { selectTeamsList, createGetRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import { PredefinedModal, MODAL_TYPE, ModalState } from '../common/Modals'
import EmptyState from 'components/Shared/EmptyState'
import styled from 'particles/themes/styled'
import { GridColDef, GridCellParams, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import TeamTable from '../TeamTable'
import Text from 'particles/Text'
import { useCheckTeamPermissionFunction, usePermission } from 'hooks/permissions'
import {
    Brand,
    CampaignStatusControl,
    OtherExternalTeamManagement,
    OwnTeamManagement,
} from 'components/Shared/Permission/enums'
import { selectTeamFilter } from 'state/filter'
import { selectIsInternalUser } from 'state/profile'
import RectangleButton from 'particles/Button/RectangleButton'
import Menu from 'particles/Menu'
import MenuItem from 'particles/MenuItem'
import Dropdown from 'particles/Dropdown'
import Search from 'particles/Search'
import { MAX_PAGE_SIZE, selectActivePageNum, setActivePageNum } from 'state/review'
import { parseBackendDate } from 'services/dates'
import { getTeamTypeOptions } from './utils'

interface TeamsListProps {
    className?: string
}

const TeamsList: React.FC<TeamsListProps> = ({ className }) => {
    const [t] = useNewTranslation()
    const SEARCH_BY_OPTIONS = [
        { value: 'name', label: t('manageTeams.allTeams.teamNameSearchMenu') },
        { value: 'member_email', label: t('manageTeams.allTeams.memberEmailSearchMenu') },
    ]
    const [openModal, setOpenModal] = useState<ModalState>({ team: undefined, type: undefined })
    const [searchTerm, setSearch] = useState<string>('')
    const [searchByOption, setSearchByOption] = useState<string>(SEARCH_BY_OPTIONS[0].value)
    const dispatch = useDispatch()
    const teams = useSelector(selectTeamsList)
    const isLoading = useSelector(createGetRequestsAreLoading([STATE_KEYS.TeamsList]))
    const activePageNum = useSelector(selectActivePageNum)
    const isInternalUser = useSelector(selectIsInternalUser)
    const isAbleToCreateTeam =
        usePermission(OwnTeamManagement.CreateTeam) || usePermission(OtherExternalTeamManagement.AssignRoles)
    const history = useHistory()
    const isCreateEditBrandPermitted = usePermission(Brand.CreateEdit)
    const teamTypeLabels = getTeamTypeOptions(t)
    const checkTeamPermission = useCheckTeamPermissionFunction()
    const canPayCampaign = usePermission(CampaignStatusControl.PayCampaign)
    const [menuState, setMenuState] = useState<{
        anchorEl: null | HTMLButtonElement
        selectedTeam?: TeamState
    }>({
        anchorEl: null,
        selectedTeam: undefined,
    })

    const handleOpenMenu = (selectedTeam: TeamState) => (event: React.MouseEvent<HTMLButtonElement>) => {
        setMenuState({ anchorEl: event.currentTarget, selectedTeam })
    }
    const handleCloseMenu = () => {
        setMenuState({
            anchorEl: null,
            selectedTeam: undefined,
        })
    }

    const onClearSearchTerm = (): void => setSearch('')

    const onCloseModal = (): void => setOpenModal({ team: openModal.team, type: undefined })

    const onOpenModal = (type: MODAL_TYPE | undefined, team?: TeamState): void => setOpenModal({ type, team })

    const teamFilter = useSelector(selectTeamFilter)

    const fetchData = (offset?: number) => dispatch(getTeamsList({ [searchByOption]: searchTerm, teamFilter, offset }))

    useEffect(() => {
        dispatch({ type: TeamActions.CLEAR_TEAM_LIST })
        if (teams.length === 0) {
            dispatch(fetchData())
        } else {
            const offset: number = activePageNum * MAX_PAGE_SIZE
            dispatch(fetchData(offset))
        }
        return (): void => {
            dispatch({ type: TeamActions.CLEAR_TEAM_LIST })
        }
    }, [activePageNum, searchTerm, searchByOption])

    const noData = !isLoading && teams && teams.length === 0
    const tableData: TeamState[] = useMemo(() => teams, [teams])

    const TEAM_COLUMN: GridColDef[] = [
        {
            field: 'name',
            headerName: t('manageTeams.allTeams.teamNameTableColumnLabel'),
            width: 400,
            cellClassName: 'title-cell',
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            sortable: false,
        },
        {
            field: 'type',
            headerName: t('manageTeams.allTeams.teamTypeTitle'),
            width: 160,
            cellClassName: 'title-cell',
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            sortable: false,
        },
        {
            field: 'member_count',
            headerName: t('manageTeams.allTeams.userTableColumnLabel'),
            width: 100,
            cellClassName: 'title-cell',
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            sortable: false,
        },
        {
            field: 'creator',
            headerName: t('manageTeams.allTeams.creatorTableColumnLabel'),
            minWidth: 160,
            flex: 1,
            cellClassName: 'title-cell',
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            sortable: false,
            maxWidth: 400,
        },
        {
            field: 'created_at',
            headerName: t('manageTeams.allTeams.dateTableColumnLabel'),
            minWidth: 160,
            width: 160,
            cellClassName: 'title-cell',
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            sortable: false,
        },
        {
            field: 'actions',
            headerName: t('manageTeams.allTeams.settingsTableColumnLabel'),
            minWidth: 160,
            flex: 1,
            cellClassName: 'action-cell',
            renderCell: function CellText(params: GridCellParams): React.ReactElement {
                const team = params.row.data
                return (
                    <RectangleButton
                        form="outlined"
                        size="s"
                        color="neutral"
                        icon="gear"
                        label={t('button.editSettings')}
                        onClick={handleOpenMenu(team)}
                        data-testid="test-team-settings-button"
                    />
                )
            },
            sortable: false,
        },
    ]
    const TEAM_LIST_ROWS: any[] = useMemo(() => {
        return tableData.map((data: any) => {
            return {
                id: data.uuid,
                name: { value: data.name },
                member_count: { value: String(data.member_count) },
                creator: { value: data.creator?.email },
                created_at: { value: parseBackendDate(data?.created_at) },
                data: data,
                selectId: `team-tooltip-${data.uuid}`,
                type: {
                    value: data.type ? teamTypeLabels[data.type]?.title : undefined,
                },
            }
        })
    }, [tableData])

    return (
        <>
            <div className={className}>
                <div className="team-page-header">
                    <div>
                        <Text scale="H4">{t('manageTeams.allTeamsPageTitle')}</Text>
                    </div>

                    <div className="team-page-second-header">
                        {isAbleToCreateTeam && (
                            <RectangleButton
                                label={t('button.createTeam')}
                                size="m"
                                form="filled"
                                icon="plus"
                                onClick={(): void => onOpenModal(MODAL_TYPE.CREATE_TEAM)}
                            />
                        )}
                        <div />
                        {isInternalUser && (
                            <div className="search-container">
                                <Dropdown
                                    width={160}
                                    onChange={value => {
                                        dispatch(setActivePageNum(0))
                                        setSearchByOption(value as string)
                                    }}
                                    value={searchByOption}
                                    size="s"
                                >
                                    {SEARCH_BY_OPTIONS.map((item, index) => (
                                        <MenuItem.Text
                                            key={`search-dropdown-${index}`}
                                            text={item.label}
                                            value={item.value}
                                            tabIndex={index}
                                        />
                                    ))}
                                </Dropdown>
                                <Search
                                    searchValue={searchTerm}
                                    width="332px"
                                    onSearch={(value: string | null): void => {
                                        dispatch(setActivePageNum(0))
                                        setSearch(value || '')
                                    }}
                                    onClearSearch={onClearSearchTerm}
                                    placeholder={t('placeholderText.enterTeamEmail')}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="team-page-table">
                    {noData ? (
                        <EmptyState
                            my={theme.spacing.m}
                            title={t('emptyState.cantFindTitle')}
                            body={t('emptyState.noTeamSearchResultSubtitle')}
                        />
                    ) : (
                        <TeamTable
                            isLoading={isLoading}
                            rows={TEAM_LIST_ROWS}
                            columns={TEAM_COLUMN}
                            hideFooter={false}
                        />
                    )}
                </div>
            </div>
            <PredefinedModal onClose={onCloseModal} {...openModal} />
            <Menu
                size="s"
                open={Boolean(menuState.anchorEl && menuState.selectedTeam)}
                anchorEl={menuState.anchorEl}
                onClose={handleCloseMenu}
                width={146}
            >
                <MenuItem.Title text={t('manageTeams.allTeams.memberMenuSectionHeader')} />
                {menuState.selectedTeam && checkTeamPermission(menuState.selectedTeam, true) && (
                    <MenuItem.Text
                        text={t('button.addMember')}
                        onClick={() => {
                            handleCloseMenu()
                            onOpenModal(MODAL_TYPE.CREATE_MEMBER, menuState.selectedTeam)
                        }}
                        tabIndex={1}
                    />
                )}
                <MenuItem.Text
                    text={t('button.viewMember')}
                    onClick={() => {
                        handleCloseMenu()
                        history.push(`team/${menuState.selectedTeam && menuState.selectedTeam.uuid}/members`)
                    }}
                    tabIndex={1}
                />
                {!menuState.selectedTeam?.is_internal && (
                    <>
                        <MenuItem.Divider />
                        <MenuItem.Title text={t('manageTeams.allTeams.brandMenuSectionHeader')} />
                        {isCreateEditBrandPermitted && (
                            <MenuItem.Text
                                text={t('button.createBrand')}
                                onClick={() => {
                                    handleCloseMenu()
                                    onOpenModal(MODAL_TYPE.CREATE_BRAND, menuState.selectedTeam)
                                }}
                                tabIndex={1}
                                size="s"
                            />
                        )}
                        <MenuItem.Text
                            text={t('button.viewBrand')}
                            onClick={() => {
                                handleCloseMenu()
                                history.push(`team/${menuState.selectedTeam && menuState.selectedTeam.uuid}/brands`)
                            }}
                            tabIndex={1}
                            size="s"
                            dataTestId="test-view-brand-menu-item"
                        />
                    </>
                )}
                <MenuItem.Divider />
                <MenuItem.Title text={t('manageTeams.allTeams.teamMenuSectionHeader')} />
                <MenuItem.Text
                    text={t('button.viewRoles')}
                    onClick={() => {
                        handleCloseMenu()
                        history.push(`teams/${menuState.selectedTeam && menuState.selectedTeam.uuid}/roles`)
                    }}
                    tabIndex={1}
                />

                {!menuState.selectedTeam?.is_internal && (isInternalUser || canPayCampaign) && (
                    <MenuItem.Text
                        text={t('button.viewBillinfo')}
                        onClick={() => {
                            handleCloseMenu()
                            history.push(
                                `teams/${menuState.selectedTeam && menuState.selectedTeam.uuid}/business-details`,
                            )
                        }}
                        tabIndex={1}
                    />
                )}
                {menuState.selectedTeam && checkTeamPermission(menuState.selectedTeam, true) && (
                    <MenuItem.Text
                        text={t('button.editTeamName')}
                        onClick={() => {
                            handleCloseMenu()
                            onOpenModal(MODAL_TYPE.EDIT_TEAM_NAME, menuState.selectedTeam)
                        }}
                        tabIndex={1}
                    />
                )}
                {menuState.selectedTeam && checkTeamPermission(menuState.selectedTeam, true) && (
                    <MenuItem.Text
                        text={t('button.deleteTeam')}
                        onClick={() => {
                            handleCloseMenu()
                            onOpenModal(MODAL_TYPE.DELETE_TEAM, menuState.selectedTeam)
                        }}
                        tabIndex={1}
                        textColor="error"
                    />
                )}
            </Menu>
        </>
    )
}

export default styled(TeamsList)`
    width: 100%;
    max-width: 1920px;
    padding: 16px 24px;
    box-sizing: border-box;
    @media screen and (max-width: 768px) {
        padding: 16px 16px;
    }
    .search-container {
        display: flex;
        align-items: flex-start;
        gap: ${({ theme }) => theme.spacing[200]};
    }
    .team-page-table {
        display: flex;
        height: fit-content;
        flex-direction: column;
        background-color: white;
        padding: 8px;
        max-width: 100%;
        box-sizing: border-box;
        margin: 0 auto;
    }
    .action-cell {
        overflow: visible !important;
    }
    .team-page-header {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-bottom: 16px;
    }
    .team-page-second-header {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        form {
            display: flex;
            align-items: center;
            gap: 8px;
            @media screen and (max-width: 768px) {
                width: 100%;
            }
        }
        @media screen and (max-width: 768px) {
            gap: 16px;
            align-items: flex-start;
            flex-direction: column;
        }
    }
`
